/**
 * 复盘台：双盲揭示复盘 + 断言告警时间线 + 批注回放 + 覆盖度统计 + 战报导出。
 *
 * 数据全部来自 `useArena()` 权威状态；战报由纯函数
 * `buildMatchReportMarkdown` 构建（与真实 P2P 后端复用同一实现）。
 */
import { useMemo, useState } from 'react';
import { AI_GLASSES_MOTION } from '@zongheng/core-schema';
import { TopBar } from '../app/TopBar.tsx';
import { useArena } from '../state/ArenaContext.tsx';
import { Badge } from '../components/ui/primitives.tsx';
import { RevealReview } from '../components/review/RevealReview.tsx';
import { ReviewAlertTimeline } from '../components/review/ReviewAlertTimeline.tsx';
import { ReviewAnnotations } from '../components/review/ReviewAnnotations.tsx';
import { CoverageSummary } from '../components/review/CoverageSummary.tsx';
import { ReviewReportPreview } from '../components/review/ReviewReportPreview.tsx';
import { ReviewAnalyticsPanel } from '../components/coach-console/ReviewAnalyticsPanel.tsx';
import { useCoachScores } from '../components/coach-console/scoreStore.ts';
import { buildMatchReportMarkdown, copyMarkdown, downloadMarkdown } from '../export/report.ts';

const REPORT_FILENAME = 'zongheng-match-report.md';

export default function ReviewPage() {
  const { state, actions } = useArena();
  const motion = AI_GLASSES_MOTION;
  const scores = useCoachScores();
  const [status, setStatus] = useState('');
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);

  const markdown = useMemo(
    () => buildMatchReportMarkdown(motion, state, { scores }),
    [motion, state, scores],
  );

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    setStatus('正在生成《纵横杯跟场诊断战报》…');
    try {
      let backendNote = '已调用后端 exportReport()';
      try {
        await actions.exportReport();
      } catch (error) {
        backendNote = `后端 exportReport() 失败：${
          error instanceof Error ? error.message : String(error)
        }`;
      }
      const copiedOk = await copyMarkdown(markdown);
      setCopied(copiedOk);
      // 「导出战报」同时触发 .md 下载（浏览器 E2E 依赖 download 事件；剪贴板失败时也兜底）
      downloadMarkdown(REPORT_FILENAME, markdown);
      setStatus(
        copiedOk
          ? `战报已下载为 ${REPORT_FILENAME}，并复制到剪贴板（${backendNote}）。`
          : `战报已下载为 ${REPORT_FILENAME}（剪贴板不可用；${backendNote}）。`,
      );
    } finally {
      setExporting(false);
    }
  };

  const handleCopy = async () => {
    const ok = await copyMarkdown(markdown);
    setCopied(ok);
    setStatus(ok ? '已复制战报到剪贴板。' : '剪贴板不可用，请使用「下载 .md」。');
  };

  const handleDownload = () => {
    downloadMarkdown(REPORT_FILENAME, markdown);
    setStatus('已触发 Markdown 下载（zongheng-match-report.md）。');
  };

  return (
    <div className="min-h-screen">
      <TopBar compact />
      <main className="mx-auto max-w-[1500px] space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-lg font-semibold tracking-wide text-slate-100">复盘台 · 跟场诊断</h1>
            <p className="mt-0.5 text-xs text-slate-400">{motion.motion}</p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="blind">揭示 {state.reveals.length}</Badge>
            <Badge tone={state.alerts.length > 0 ? 'violation' : 'pass'}>
              告警 {state.alerts.length}
            </Badge>
            <Badge tone="pass">完成 {state.completedStages.length}/16</Badge>
            <Badge tone="warning">批注 {state.annotations.length}</Badge>
          </div>
        </div>

        {state.stageIndex === 0 && state.reveals.length === 0 && (
          <p className="rounded-lg border border-clock-warning/30 bg-clock-warning/10 px-3 py-2 text-xs text-clock-warning">
            本场尚无比赛数据：返回教练台开始比赛后，复盘内容会实时同步。
          </p>
        )}

        <ReviewAnalyticsPanel motion={motion} />

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="space-y-4">
            <RevealReview />
            <ReviewAlertTimeline />
          </div>
          <div className="space-y-4">
            <CoverageSummary motion={motion} />
            <ReviewAnnotations />
          </div>
        </div>

        <ReviewReportPreview
          markdown={markdown}
          status={status}
          copied={copied}
          exporting={exporting}
          onExport={handleExport}
          onCopy={handleCopy}
          onDownload={handleDownload}
        />
      </main>
    </div>
  );
}
